import Location from "./collisionWarning/Location";
import moment from "moment";
import * as CollisionLogicUtil from "./collisionWarning/CollisionLogicUtil";
import * as LightWeightGlobalLogic from "./collisionWarning/LightWeightGlobalLogic";
import { VESSEL_CATEGORY, NAVIGATION_STATUS } from "@/config/const";

export default class VesselMovement {
  constructor(timestamp, location, direction, heading, velocity, isExpiring) {
    this._timestamp = timestamp ? moment(timestamp).utc() : moment().utc();
    this._location = new Location(location.latitude, location.longitude);
    this._direction = direction;
    this._heading = heading;
    this._velocity = velocity;
    this._isExpiring = isExpiring;
  }

  isActive() {
    return this._velocity > 1.0;
  }

  get timestamp() {
    return this._timestamp;
  }

  get location() {
    return this._location;
  }

  get direction() {
    return this._direction;
  }

  get heading() {
    return this._heading;
  }

  get velocity() {
    return this._velocity;
  }

  get isExpiring() {
    return this._isExpiring;
  }
}


export const getCenterCoordinationFromAIS = (info, position) => {
  let latitude = position.l.latitude || position.l._lat;
  let longitude = position.l.longitude || position.l._long;
  if (!info || !info.w || !info.l) {
    return new Location(latitude, longitude);
  }

  const width = info.w;
  const length = info.l;

  const starboardLen = info.d_sb ? info.d_sb : width / 2.0;
  const portLen = info.d_p ? info.d_p : width / 2.0;
  const bowLen = info.d_b ? info.d_b : length / 2.0;
  const sternLen = info.d_s ? info.d_s : length / 2.0;

  const moveForStarboard = starboardLen - (starboardLen + portLen) / 2.0;
  const starboardDirection = (position.h + 90.0) - 360.0 * Math.ceil((position.h + 90.0) / 360.0);
  const middle = LightWeightGlobalLogic.getArrivalPoint(new Location(latitude, longitude), starboardDirection, moveForStarboard);

  const moveForStern = sternLen - (sternLen + bowLen) / 2.0;
  const sternDirection = (position.h + 180.0) - 360.0 * Math.ceil((position.h + 180.0) / 360.0);

  return LightWeightGlobalLogic.getArrivalPoint(middle, sternDirection, moveForStern);
};

export const isActive = (velocity) => {
  return velocity > 1.0;
};

export const getSymbolIconName = (vesselInfo, movement, isPrefix) => {
  const target = movement.t ? movement.t : moment().utc();
  const seven = moment().utc().subtract(7, "minutes");
  let iconName = "vessel";
  let category = (vesselInfo != null) ? vesselInfo.c : VESSEL_CATEGORY.OTHER;
  switch (category) {
    case VESSEL_CATEGORY.TANKER: // Tanker
      iconName += `_tanker${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.CARGO: // Cargo
      iconName += `_cargo${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.TUGBOAT: // Tugboat
      iconName += `_tug${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.CRUISE: // Cruise
      iconName += `_passenger${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.FISHING: // Fishing
      iconName += `_fishing${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.PLEASURE: // Pleasure
      iconName += `_app${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.JET_SKI: // JetSki
      iconName += `_bike${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.OTHER: // Other
      iconName += `_other${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.WORK: // Work
      iconName += `_work${
          isActive(movement.v)
              ? "_active"
              : seven.isAfter(target)
              ? "_disable"
              : ""
          }`;
      break;
    case VESSEL_CATEGORY.SAILBOAT: // Sailboat
      iconName += `_sailboat${
          isActive(movement.v)
              ? "_active"
              : seven.isAfter(target)
              ? "_disable"
              : ""
          }`;
      break;
    case VESSEL_CATEGORY.BARGE: // Barge
      iconName += `_barge${
          isActive(movement.v)
              ? "_active"
              : seven.isAfter(target)
              ? "_disable"
              : ""
          }`;
      break;
    default:
      iconName += `_other${
        isActive(movement.v)
          ? "_active"
          : seven.isAfter(target)
          ? "_disable"
          : ""
        }`
      break;
  }

  if (isPrefix) {
    return "icon_" + iconName;
  }

  return (iconName += "_icon");
};

export const getSymbolIconNameLightWeight = (category, velocity, diff) => {
  let iconName = "v";
  switch (category) {
    case VESSEL_CATEGORY.TANKER: // Tanker
      iconName += `_tan${
        isActive(velocity)
          ? "-a"
          : ((diff) >= 60*7)
          ? "-d"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.CARGO: // Cargo
      iconName += `_car${
        isActive(velocity)
          ? "-a"
          : ((diff) >= 60*7)
          ? "-d"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.TUGBOAT: // Tugboat
      iconName += `_tug${
        isActive(velocity)
          ? "-a"
          : ((diff) >= 60*7)
          ? "-d"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.CRUISE: // Cruise
      iconName += `_pas${
        isActive(velocity)
          ? "-a"
          : ((diff) >= 60*7)
          ? "-d"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.FISHING: // Fishing
      iconName += `_fis${
        isActive(velocity)
          ? "-a"
          : ((diff) >= 60*7)
          ? "-d"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.PLEASURE: // App
      iconName += `_ple${
        isActive(velocity)
          ? "-a"
          : ((diff) >= 60*7)
          ? "-d"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.JET_SKI: // JetSki
      iconName += `_bik${
        isActive(velocity)
          ? "-a"
          : ((diff) >= 60*7)
          ? "-d"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.OTHER: // Other
      iconName += `_oth${
        isActive(velocity)
          ? "-a"
          : ((diff) >= 60*7)
          ? "-d"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.WORK: // Work
      iconName += `_wor${
        isActive(velocity)
          ? "-a"
          : ((diff) >= 60*7)
          ? "-d"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.SAILBOAT: // Sailboat
      iconName += `_sai${
        isActive(velocity)
          ? "-a"
          : ((diff) >= 60*7)
          ? "-d"
          : ""
        }`;
      break;
    case VESSEL_CATEGORY.BARGE: // Barge
      iconName += `_bar${
        isActive(velocity)
          ? "-a"
          : ((diff) >= 60*7)
          ? "-d"
          : ""
        }`;
      break;
    default:
      iconName += `_oth${
        isActive(velocity)
          ? "-a"
          : ((diff) >= 60*7)
          ? "-d"
          : ""
        }`
      break;
  }

  return iconName;
};

export const getSimpleOpacity = (vesselCategory, diff) => {
  const category = (vesselCategory != null) ? vesselCategory : VESSEL_CATEGORY.OTHER;
  switch (category) {
    default:
      return 1.0;
    case VESSEL_CATEGORY.TANKER: // Tanker
    case VESSEL_CATEGORY.CARGO: // Cargo
    case VESSEL_CATEGORY.TUGBOAT: // Tugboat
    case VESSEL_CATEGORY.CRUISE: // Cruise
    case VESSEL_CATEGORY.FISHING: // Fishing
    case VESSEL_CATEGORY.OTHER: // Other
    case VESSEL_CATEGORY.WORK: // Work
    case VESSEL_CATEGORY.SAILBOAT: // Sailboat
    case VESSEL_CATEGORY.BARGE: // Barge
      return ((diff <= 60*7) ? 1.0 : 0.5);
    case VESSEL_CATEGORY.PLEASURE: // Pleasure
    case VESSEL_CATEGORY.JET_SKI: // JetSki
      return ((diff <= 50) ? 1.0 : 0.5);
  }
};

export const getVectorOpacity = (diff) => {
  return ((diff <= 60*7) ? 0.7 : 0);
};

export const getVesselMovement = (vessel, timestamp) => {
  const positions = vessel.positions.sortBy(value => value, (valueA, valueB) => {
    return valueB.timestamp.isAfter(valueA.timestamp) ? 1 : -1;
  });
  const justBeforeIndex = positions.findIndex(value => value.timestamp.isSameOrBefore(timestamp));
  const justAfter = positions.get(justBeforeIndex - 1);
  const justBefore = positions.get(justBeforeIndex);
  const nextBefore = positions.get(justBeforeIndex + 1);

  const location = getLocation(timestamp, justAfter, justBefore, vessel.information.category.value);
  if (justBefore && justBefore.direction && justBefore.velocity && justBefore.heading) {
    return new VesselMovement(timestamp, location, justBefore.direction, justBefore.heading, justBefore.velocity > 100 ? 0 : justBefore.velocity);
  }

  if (justAfter) {
    const { direction, velocity } = getVector(justAfter, justBefore);
    if (!justBefore.direction) {
      justBefore.direction = direction;
    }
    if (!justBefore.velocity) {
      justBefore.velocity = velocity;
    }
    if (!justBefore.heading) {
      justBefore.heading = direction;
    }

    return new VesselMovement(timestamp, location, justBefore.direction, justBefore.heading, justBefore.velocity > 100 ? 0 : justBefore.velocity);
  }

  if (nextBefore && nextBefore.direction && nextBefore.velocity && nextBefore.heading) {
    return new VesselMovement(timestamp, location, nextBefore.direction, nextBefore.heading, nextBefore.velocity > 100 ? 0 : nextBefore.velocity);
  }

  if (nextBefore) {
    const { direction, velocity } = getVector(justBefore, nextBefore);
    if (!nextBefore.direction) {
      nextBefore.direction = direction;
    }
    if (!nextBefore.velocity) {
      nextBefore.velocity = velocity;
    }
    if (!nextBefore.heading) {
      nextBefore.heading = direction;
    }

    return new VesselMovement(timestamp, location, nextBefore.direction, nextBefore.heading, nextBefore.velocity > 100 ? 0 : nextBefore.velocity);
  }

  const direction = justBefore.direction ? justBefore.direction : 0;
  const heading = justBefore.heading ? justBefore.heading : direction;
  return new VesselMovement(timestamp, location, direction, heading, 0);
};

const getVector = (after, before) => {
  if (after.location.latitude === before.location.latitude && after.location.longitude === before.location.longitude) {
    return {
      direction: 0,
      velocity: 0
    };
  }
  const move = LightWeightGlobalLogic.getMovement(
    new Location(before.location.latitude, before.location.longitude),
    new Location(after.location.latitude, after.location.longitude)
  );
  let dir = 0;
  if (isFinite(move.direction)) {
    dir = move.direction;
  }
  const timeDiff = Math.max(after.timestamp.diff(before.timestamp), 0.0001);
  const vel = move.distance * 60.0 * 60.0 / timeDiff / CollisionLogicUtil.MILE;
  return {
    direction: dir,
    velocity: vel
  };
};

export const getVectorWithLightWeight = (after, before) => {
  if (after.l.latitude === before.l.latitude && after.l.longitude === before.l.longitude) {
    return {
      direction: 0,
      velocity: 0
    };
  }
  const move = LightWeightGlobalLogic.getMovement(
    new Location(before.l.latitude, before.l.longitude),
    new Location(after.l.latitude, after.l.longitude)
  );
  let dir = 0;
  if (isFinite(move.direction)) {
    dir = move.direction;
  }
  const timeDiff = Math.max(after.t - before.t, 0.0001);
  const vel = move.distance * 60.0 * 60.0 / timeDiff / CollisionLogicUtil.MILE;
  return {
    direction: dir,
    velocity: vel
  };
};

const getLocation = (timestamp, justAfter, justBefore, vesselCategoryId) => {
  const timeBefore = timestamp.diff(justBefore.timestamp);
  if (justAfter) {
    const timeAfter = justAfter.timestamp.diff(timestamp);
    return new Location(
      (timeAfter * justBefore.location.latitude + timeBefore * justAfter.location.latitude) / (timeAfter + timeBefore),
      (timeAfter * justBefore.location.longitude + timeBefore * justAfter.location.longitude) / (timeAfter + timeBefore)
    );
  }

  const getExpirationTime = () => {
    switch (vesselCategoryId) {
      default:
        return 7.0 * 60.0;
      case VESSEL_CATEGORY.PLEASURE:
      case VESSEL_CATEGORY.JET_SKI:
        return 60.0;
    }
  };

  if (timestamp.isSameOrAfter(justBefore.timestamp.add(getExpirationTime(), "seconds"))) {
    return new Location(justBefore.location.latitude, justBefore.location.longitude);
  }

  return null;
};

export const getStatusLamp = (status) => {
  let statusLamp = {
    color: null,
    blink: false
  }
  switch(status) {
    case NAVIGATION_STATUS.UNDER_WAY_USING_ENGINE:
    case NAVIGATION_STATUS.UNDER_WAY_SAILING:
      statusLamp.color = "#46B5F4"
      break;
    case NAVIGATION_STATUS.AT_ANCHOR:
    case NAVIGATION_STATUS.MOORED:
    case NAVIGATION_STATUS.RESERVED13:
    case NAVIGATION_STATUS.NOT_AVAILABLE:
    case NAVIGATION_STATUS.NOT_IN_OPARATION:
      statusLamp.color = "#A9ADAF"
      break;
    case NAVIGATION_STATUS.NOT_UNDER_COMMAND:
    case NAVIGATION_STATUS.AGROUND:
    case NAVIGATION_STATUS.SART:
      statusLamp.color = "#F45146"
      break;
    case NAVIGATION_STATUS.RESTRICTED_MANEUVERABILITY:
    case NAVIGATION_STATUS.CONSTRAINED_BY_HER_DRAUGHT:
    case NAVIGATION_STATUS.ENGAGED_IN_FISHING:
    case NAVIGATION_STATUS.RESERVED9:
    case NAVIGATION_STATUS.RESERVED10:
    case NAVIGATION_STATUS.TOWING:
    case NAVIGATION_STATUS.PUSHING:
      statusLamp.color = "#F4D946"
      break;
    default:
      statusLamp.color = "#A9ADAF"
  }
  
  switch(status) {
    case NAVIGATION_STATUS.AGROUND:
    case NAVIGATION_STATUS.RESERVED10:
    case NAVIGATION_STATUS.TOWING:
    case NAVIGATION_STATUS.PUSHING:
      statusLamp.blink = true
      break;
  }

  return statusLamp
};
